import { withApollo, WithApolloClient } from '@apollo/client/react/hoc';

import React, { useEffect, useState } from 'react';

import { loadScript, parseQueryStrings } from '@crehana/utils';
import { URLS } from '@crehana/utils/dist/loadScript';

import {
  trackUserActivity,
  UserActivityEvent,
} from 'Jsx/Analytics/UserActivity';
import trackPageView from 'Jsx/Analytics/UserActivity/trackPageView';
import RETARGETTINGS_MUTATION from 'Jsx/CheckoutV4/Graphql/Mutation/AddCheckoutRetargeting.graphql';
import APPLY_COUPON_MUTATION from 'Jsx/CheckoutV4/Graphql/Mutation/ApplyCoupon.payments.graphql';
import PAYMENT_MUTATION from 'Jsx/CheckoutV4/Graphql/Mutation/Payment.payments.graphql';
import { AddCheckoutRetargetingMutationVariables as RetargetingVariables } from 'Jsx/CheckoutV4/Graphql/Mutation/types/AddCheckoutRetargetingMutation';
import { ApplyCouponMutation } from 'Jsx/CheckoutV4/Graphql/Mutation/types/ApplyCouponMutation';
import {
  PaymentMutation,
  PaymentMutationVariables,
} from 'Jsx/CheckoutV4/Graphql/Mutation/types/PaymentMutation';
import GET_PAYMENT_METHOD_QUERY from 'Jsx/CheckoutV4/Graphql/Query/GetPaymentMethod.payments.graphql';
import GET_CHECKOUT_ITEM_QUERY from 'Jsx/CheckoutV4/Graphql/Query/GetProductItem.payments.graphql';
import { GetPaymentMethodQuery } from 'Jsx/CheckoutV4/Graphql/Query/types/GetPaymentMethodQuery';
import {
  GetProductItemQuery,
  GetProductItemQueryVariables,
} from 'Jsx/CheckoutV4/Graphql/Query/types/GetProductItemQuery';
import I18N_STRINGS from 'Jsx/CheckoutV4/i18n/language';
import {
  CustomEmail,
  ItemType,
  PaymentFormValues,
  PaymentMethods,
  PaymentMethodType,
  ProductData,
  ProductItem,
  StepsToBuy,
} from 'Jsx/CheckoutV4/types';
import { getStepsForMethod } from 'Jsx/CheckoutV4/utils';
import formatPaymentMethods from 'Jsx/CheckoutV4/utils/formatter/formatPaymentMethods';
import { GtagItem } from 'Jsx/global';
import { recollectAndSendFBTracks } from 'Jsx/Utils/facebookTracking/facebookTracking';
// eslint-disable-next-line no-restricted-imports
import getQueryParam from 'Jsx/Utils/getQueryParam';

import createRecursiveFunctionForAnalytics from 'Utils/createRecursiveFunctionForAnalytics';

import { AppProps } from './App';
import { METHODS } from './common/constants';
import { CheckoutProvider } from './Context';
import CheckoutPages from './pages';
import { ShoppingCartItem } from './types/types';

/**
 * This only return custom membership tag name for membership itemType,
 * for other cases it returns the regular itemType
 */
const getItemType = (itemType: string | null, itemTagName: string | null) => {
  if (itemType === 'membership' && itemTagName) {
    return itemTagName.toLowerCase();
  }

  return itemType;
};

interface CheckoutSectionProps extends WithApolloClient<AppProps> {
  itemData: {
    itemId: number;
    itemType: ItemType;
  };
  preloadedCourseData?: any;
  recaptchaToken: string;
  isGuestCheckout: boolean;
  isCallCenter: boolean;
}

const Checkout: React.FC<CheckoutSectionProps> = props => {
  const {
    client,
    countryPrefix,
    isCallCenter,
    isGuestCheckout,
    itemData,
    preloadedCourseData,
    productData,
    recaptchaToken,
    shoppingCartItems,
    user,
    flags,
  } = props;

  const [isFetchingCheckoutItemDetail, setIsFetchingCheckoutItemDetail] =
    useState(true);
  const [checkoutProductsList, setCheckoutProductsList] = useState<
    ShoppingCartItem[]
  >([]);
  const [isFetchingPaymentMethods, setIsFetchingPaymentMethods] =
    useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethods>({});
  const [guestEmail, setGuestEmail] = useState<{
    email: string;
    errorTextEmail: string;
    isEmailValid: boolean;
  }>({
    email: '',
    errorTextEmail: '',
    isEmailValid: false,
  });
  const [checkoutProductDetail, setCheckoutProductDetail] =
    useState<ProductItem | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodType>();
  const [selectedOfflinePaymentMethod, setSelectedOfflinePaymentMethod] =
    useState<PaymentMethodType>();
  const [onlinePaymentMethods, setOnlinePaymentMethods] = useState<
    PaymentMethodType[]
  >([]);
  const [itemBeforeCouponApplication, setItemBeforeCouponApplication] =
    useState<ProductItem>();
  const [stepsToBuy, setStepsToBuy] = useState<StepsToBuy[]>([
    {
      order: 1,
      label: 'Proceso de pago',
      status: 'current',
    },
    {
      order: 2,
      label: 'Gracias!',
      status: 'disabled',
    },
  ]);
  const [offlinePaymentMethods, setOfflinePaymentMethods] =
    useState<PaymentMethods>();
  const [paymentFormValues, setPaymentFormValues] = useState({
    creditCardCVV: {
      isComplete: false,
      value: '',
    },
    creditCardExpiry: {
      isComplete: false,
      value: '',
    },

    creditCardNumber: {
      isComplete: false,
      value: '',
    },
    termsAndConditions: {
      isComplete: true,
    },
    checkedNewsLetterTerms: {
      value: false,
    },
  });

  const getDataForTracking = (data: ProductItem | null) => {
    if (data) {
      const itemType = getItemType(data.type, data.membershipTag);
      let dataForTracking;

      if (data.type === 'course') {
        dataForTracking = {
          product_id: data.id,
          item_type: itemType,
          course_id: data.id,
        };
      } else if (data.type === 'membership') {
        dataForTracking = {
          product_id: data.id,
          item_type: itemType,
          course_id: null,
          price: data.totalToPay,
        };
      } else {
        dataForTracking = {
          product_id: data.id,
          item_type: itemType,
          course_id: null,
        };
      }

      return dataForTracking;
    }
  };

  const sendTrackUserActivity = (
    newItemData: ProductData,
    fullProductData?: ProductItem,
  ) => {
    const isSingleProduct = shoppingCartItems?.length === 0;

    if (isSingleProduct) {
      trackUserActivity({
        event: UserActivityEvent.CHECKOUT,
        // @ts-expect-error
        data: getDataForTracking(fullProductData),
        user: user
          ? { segment: user.segmentationKey!, username: user.username! }
          : undefined,
      });
    } else {
      shoppingCartItems?.forEach(product => {
        trackUserActivity({
          event: UserActivityEvent.CHECKOUT,
          data: {
            // @ts-expect-error
            ...getDataForTracking({ ...product, type: 'course' }),
            /* We overwrite the product_id here, because this is a list of items,
               and we can get it from the fullProductData */
            // @ts-expect-error
            item_type: newItemData.itemType,
            // @ts-expect-error
            product_id: parseInt(newItemData.itemId, 10),
          },
          user: user
            ? { segment: user.segmentationKey!, username: user.username! }
            : undefined,
        });
      });
    }
  };

  const getForceMethodVariable = (): number | null => {
    const ref = getQueryParam('force_method') as string | undefined;

    const methodId = ref && /[0-9]+/.test(ref) ? parseInt(ref, 10) : null;

    return methodId;
  };

  const sendTracks = () => {
    trackPageView({
      // @ts-expect-error
      user,
      type: productData.itemType,
      id: productData.itemId,
      page: 'checkout',
    });
  };

  const fetchPaymentMethods = ({
    itemId,
    itemType,
    forceMethod,
  }: {
    itemId: number;
    itemType: string;
    forceMethod?: number | null;
  }) => {
    setIsFetchingPaymentMethods(true);
    const queryParams = parseQueryStrings(window.location.search);
    let isDegreeQuota = false;

    if (queryParams?.is_degree_quota === 'true') {
      isDegreeQuota = true;
    }

    client!
      .query({
        query: GET_PAYMENT_METHOD_QUERY,
        variables: {
          itemType,
          itemId,
          forceMethod,
          isDegreeQuota: itemType === 'degree' && isDegreeQuota,
        },
        context: { clientName: 'payments' },
      })
      .then(({ data }: { data: GetPaymentMethodQuery }) => {
        if (data?.paymentMethod) {
          const {
            paymentMethods: fetchedPaymentMethods,
            offlinePaymentMethods: fetchedOfflinePaymentMethods,
            onlinePaymentMethods: fetchedOnlinePaymentMethods,
            selectedOnlinePaymentMethod,
          } = formatPaymentMethods(data.paymentMethod);

          const selectedPaymentMethodTitle = selectedOnlinePaymentMethod?.title;

          if (!props.flags.useNewCreditCardFeature) {
            if (selectedPaymentMethodTitle === METHODS.CONEKTA_CARD) {
              loadScript({ src: URLS.conekta });
            } else if (
              selectedPaymentMethodTitle === METHODS.EBANX_CARD ||
              selectedPaymentMethodTitle === METHODS.EBANX_CARD_AR
            ) {
              loadScript({ src: URLS.ebanx });
            }

            if (selectedPaymentMethodTitle) {
              setStepsToBuy(
                getStepsForMethod(
                  selectedPaymentMethodTitle,
                  itemType === 'product',
                  isGuestCheckout,
                ),
              );
            }
          }

          setPaymentMethods(fetchedPaymentMethods);
          setOfflinePaymentMethods(fetchedOfflinePaymentMethods);
          setOnlinePaymentMethods(fetchedOnlinePaymentMethods);
        }
      })
      .catch((err: { message: string }) => {
        console.log({ err });
      })
      .finally(() => {
        setIsFetchingPaymentMethods(false);
      });
  };

  const googleAnalyticsCheckout = (product: {
    itemId: number;
    title: string;
    membershipTag: string;
    itemType: string;
  }) => {
    createRecursiveFunctionForAnalytics(() => {
      const cartItemsList = shoppingCartItems;
      let items: GtagItem[] = [];

      if (cartItemsList && cartItemsList.length > 0) {
        cartItemsList.forEach(
          ({ id, title }: { id: number; title: string }) => {
            const item = {
              id: `COURSE_${id}`,
              name: title,
            };

            items = [...items, item];
          },
        );
      } else {
        items = [
          {
            id: `${product.itemType.toUpperCase()}_${product.itemId}`,
            name: product.membershipTag || product.title,
          },
        ];
      }

      if (window.gtag) {
        window.gtag('event', 'begin_checkout', {
          send_to: 'UA-60829200-1',
          items,
        });
      }
    })();
  };

  const fetchCheckoutItemDetail = (variables: GetProductItemQueryVariables) => {
    setIsFetchingCheckoutItemDetail(true);
    client!
      .query<GetProductItemQuery>({
        query: GET_CHECKOUT_ITEM_QUERY,
        variables,
        context: { clientName: 'payments' },
      })
      .then(({ data }) => {
        if (data?.item) {
          const { item } = data;
          const fetchedCheckoutProductDetail: ProductItem = {
            ...item,
            originalId: item.id,
            subtotal: item.price,
            price: item.total,
            discountRate: item.totalPercentDiscount,
            terms: parseInt(getQueryParam('terms') as string, 10),
            isQuickEnroll: item.total === 0,
            couponCode: productData?.pageCoupon?.isValid
              ? productData?.pageCoupon?.couponCode
              : '',
            membershipStartedDate: getQueryParam('start_date') as string,
            giftcard: getQueryParam('giftcard') as string,
            promotionToken: productData?.callCenter?.token || '',
            realPrice: item.realPrice,
          };

          if (
            item.type === 'custom_bundle' ||
            item.type === 'bundle' ||
            item.type === 'shopping_cart'
          ) {
            setCheckoutProductsList(shoppingCartItems || []);
          }
          setCheckoutProductDetail(fetchedCheckoutProductDetail);
          setItemBeforeCouponApplication(fetchedCheckoutProductDetail);
          setIsFetchingCheckoutItemDetail(false);

          const event = new CustomEvent('item-data-has-loaded', {
            detail: {
              checkoutProductDetail,
            },
          });

          document.dispatchEvent(event);

          // DATALAYER FOR GOOGLE ADS DYNAMIC REMARKETING
          let dynxItemId = checkoutProductDetail?.originalId?.toString();
          let dynxItemId2 = `${fetchedCheckoutProductDetail.type}_${fetchedCheckoutProductDetail.originalId}`;
          const itemIds = shoppingCartItems
            ?.map?.(course => course.id)
            .toString();

          if (shoppingCartItems && shoppingCartItems?.length > 0) {
            dynxItemId = itemIds;
            dynxItemId2 = shoppingCartItems
              .map(course => `course_${course.id}`)
              .toString();
          }

          (window.dataLayer || []).push({
            google_tag_params: {
              dynx_itemid: dynxItemId,
              dynx_itemid2: dynxItemId2,
              dynx_pagetype: 'conversionintent',
              dynx_totalvalue: fetchedCheckoutProductDetail.totalToPay,
            },
          });
          /** Begin Facebook Pixel */
          recollectAndSendFBTracks();

          let contentName = item.type;

          if (item.type === 'degree') {
            contentName = 'Data Analytics';
          } else if (item.type === 'membership') {
            contentName = `${item.type}_${item.membershipTag?.toLowerCase()}`;
          } else if (item.type === 'course') {
            contentName = item.title;
          }

          let contentCategory = item.type;

          if (item.type === 'membership') {
            contentCategory = 'premium';
          } else if (item.type === 'course') {
            contentCategory = `${item.category} > ${item.subCategory}`;
          }

          window?.fbq?.('track', 'InitiateCheckout', {
            content_name: contentName,
            content_category: contentCategory,
            content_ids: itemIds?.length! > 0 ? itemIds : [item.id!.toString()],
            content_type: 'product',
            order_item_type: item.type,
            value: item.totalToPay,
            currency: 'USD',
          });

          /** End Facebook Pixel */
          googleAnalyticsCheckout({
            itemId: item.id || 0,
            title: item.title || '',
            membershipTag: item.membershipTag || '',
            itemType: item.type || '',
          });
        } else {
          setIsFetchingCheckoutItemDetail(false);
        }
      })
      .catch(() => {
        setIsFetchingCheckoutItemDetail(false);
      });
  };

  const checkoutRetargetingMutation = (variables: RetargetingVariables) => {
    client!
      .mutate({
        mutation: RETARGETTINGS_MUTATION,
        variables,
      })
      .then(() => {});
  };

  useEffect(() => {
    const newItemData = itemData || productData;

    document.addEventListener(
      'item-data-has-loaded',
      (
        e: EventInit & {
          detail?: { checkoutProductDetail: ProductItem };
        },
      ) => {
        try {
          sendTrackUserActivity(
            // @ts-expect-error
            newItemData,
            e.detail?.checkoutProductDetail,
          );
        } catch (error) {
          console.error(error);
        }
      },
      false,
    );

    const forceMethodVariable = getForceMethodVariable();

    fetchPaymentMethods({
      itemId: newItemData.itemId,
      itemType: newItemData.itemType,
      forceMethod: forceMethodVariable,
    });
    fetchCheckoutItemDetail({
      itemId: newItemData.itemId,
      itemType: newItemData.itemType,
      itemPromotionToken: productData?.callCenter?.token,
    });

    if (!isGuestCheckout) {
      checkoutRetargetingMutation({
        itemId: newItemData.itemId.toString(),
        itemType: newItemData.itemType,
      });
    }
    sendTracks();
  }, []);

  const fallbackToCreditCardPaymentMethod = (
    newSelectedPaymentMethod?: PaymentMethodType,
  ) => {
    setSelectedPaymentMethod({ ...newSelectedPaymentMethod, isSelected: true });
  };

  const updatePaymentMethods = (methods: PaymentMethods) =>
    setPaymentMethods(methods);

  const onEmailChange = (emailDetail: CustomEmail) => {
    const { value, error, complete } = emailDetail;

    setGuestEmail({
      email: value,
      errorTextEmail: error.message,
      isEmailValid: complete,
    });
  };

  const handleOnblurEmail = () => {
    if (guestEmail.email.trim() === '') {
      setGuestEmail({
        email: '',
        errorTextEmail: I18N_STRINGS.REQUIRE_EMAIL,
        isEmailValid: false,
      });
    }
  };

  const handleRemoveCoupon = () => {
    if (itemBeforeCouponApplication && checkoutProductDetail) {
      setCheckoutProductDetail({
        ...itemBeforeCouponApplication,
        couponCode: '',
      });
    }
  };

  const handleApplyCoupon = (coupon: {
    couponCode: string;
    // @ts-expect-error
  }): Promise<{
    success: boolean;
    errors: any;
  }> => {
    const { couponCode } = coupon;

    if (checkoutProductDetail) {
      const { originalId, type } = checkoutProductDetail;
      const variables = {
        itemId: originalId,
        itemType: type,
        couponCode,
      };

      // @ts-expect-error
      return client!
        .mutate({
          mutation: APPLY_COUPON_MUTATION,
          variables,
          context: { clientName: 'payments' },
        })
        .then(({ data: couponData }: { data?: ApplyCouponMutation }) => {
          if (couponData?.applyCoupon) {
            const { item, success, messages } = couponData.applyCoupon;

            if (success && item) {
              const {
                total,
                realPrice,
                totalPercentDiscount,
                couponValueDiscount,
                totalToPay,
              } = item;

              const { subtotal } = checkoutProductDetail;
              const fakePrice = subtotal! > total! ? subtotal : realPrice;

              setCheckoutProductDetail({
                ...checkoutProductDetail,
                price: total,
                realPrice: fakePrice,
                discountRate: totalPercentDiscount,
                couponCode,
                couponValueDiscount,
                totalToPay,
              });
            }

            return { success, errors: messages };
          }
        })
        .catch(error => {
          console.log({ error });
        });
    }
  };

  const getPaymentMethodType = (methodSource: string | undefined) => {
    if (methodSource === undefined) return '';

    if (
      methodSource === 'Stripe' ||
      methodSource === 'Paypal' ||
      methodSource === 'PayU Peru' ||
      methodSource === 'Stripe Mexico' ||
      methodSource === 'Ebanx Card Brasil' ||
      methodSource === 'Ebanx Card Argentina'
    )
      return 'Online';

    return 'Offline';
  };

  const updatePaymentFormValues = (values: PaymentFormValues) => {
    setPaymentFormValues({ ...paymentFormValues, ...values });
  };

  const buyNowMutation = ({
    variables,
  }: {
    variables: PaymentMutationVariables;
  }) => {
    const couponCode = checkoutProductDetail?.couponCode || '';

    try {
      if (window.gtag && checkoutProductDetail) {
        const checkoutOption = getPaymentMethodType(
          selectedOfflinePaymentMethod?.title || selectedPaymentMethod?.title,
        );

        window.gtag('event', 'checkout_progress', {
          event_category:
            checkoutProductDetail.type === 'membership' ? 'membership' : 'payg',
          send_to: 'UA-60829200-1',
          checkout_option: checkoutOption,
          checkout_step: 1,
          value: checkoutProductDetail.totalToPay || undefined,
          items: [
            {
              id: `${checkoutProductDetail.type?.toUpperCase()}_${
                checkoutProductDetail.id
              }`,
              name: checkoutProductDetail.title || undefined,
              price: checkoutProductDetail.totalToPay || undefined,
              quantity: shoppingCartItems?.length || 1,
            },
          ],
        });
      }
    } catch (error) {
      console.error(error);
    }
    const rockstarId =
      parseInt(getQueryParam('rockstar_id') as string, 10) || undefined;

    return client!
      .mutate({
        mutation: PAYMENT_MUTATION,
        variables: {
          ...variables,
          rockstarId,
          // Always add the recaptcha token to every payment to prevent crawlers or bots
          recaptchaToken,
          couponCode,
          giftcard: checkoutProductDetail?.giftcard,
        },
        context: { clientName: 'payments' },
      })
      .then(({ data }: { data?: PaymentMutation }) => {
        return data?.payment || null;
      })
      .catch((error: { message: string }) => {
        window.Sentry.withScope((scope: any) => {
          scope.setTag('paymentMethod', selectedPaymentMethod?.title);
          window.Sentry.captureException(error);
        });
      });
  };

  const isQuickEnrollEnabled =
    !isFetchingCheckoutItemDetail && checkoutProductDetail?.price === 0;

  const showPaymenButtonRight =
    !flags.useNewCreditCardFeature &&
    (selectedPaymentMethod?.title === METHODS.STRIPE ||
      selectedPaymentMethod?.title === METHODS.STRIPE_MEXICO);

  return (
    <CheckoutProvider
      value={{
        guestEmail,
        onEmailChange,
        handleOnblurEmail,
        isGuestCheckout,
        preloadedCourseData,
        user,
        isFetchingPaymentMethods,
        paymentMethods,
        checkoutProductDetail,
        isBootcamp: checkoutProductDetail?.title?.includes('Bootcamp')!,
        handleApplyCoupon,
        handleRemoveCoupon,
        setSelectedPaymentMethod,
        fallbackToCreditCardPaymentMethod,
        selectedPaymentMethod,
        buyNowMutation,
        selectedOfflinePaymentMethod,
        setSelectedOfflinePaymentMethod,
        setStepsToBuy,
        offlinePaymentMethods,
        countryPrefix,
        recaptchaToken,
        isQuickEnrollEnabled,
        checkoutProductsList,
        productData,
        stepsToBuy,
        onlinePaymentMethods,
        updatePaymentMethods,
        setPaymentFormValues: updatePaymentFormValues,
        paymentFormValues,
        showPaymenButtonRight,
        flags,
      }}
    >
      <CheckoutPages
        isFetchingCheckoutItemDetail={isFetchingCheckoutItemDetail}
        isCallCenter={isCallCenter}
      />
    </CheckoutProvider>
  );
};

// @ts-expect-error
export default withApollo(Checkout);
